/**
 * The filter row of the sync-run page.
 *
 * PARENT OWNS THE VALUES. Every control is fully controlled: this component holds
 * no filter state and resets no offset. The page keeps that because the reset
 * belongs with the pagination it invalidates — an offset carried across a filter
 * change points into the previous population and renders an empty table that
 * reads as "no results".
 *
 * NO SEARCH BOX. There is no search contract for this endpoint, and a field that
 * quietly filters nothing is worse than none. The shared `BexioFilterBar` is
 * therefore not used — it exists around a search input — while its select
 * primitive is.
 *
 * The two option lists live here rather than in `bexioSyncRunsView` because they
 * are this bar's vocabulary: the "Alle" and "Daten-Syncs" entries are filter
 * affordances, not sync-run domain values, and the domain lists they extend
 * (`SYNC_RUN_SCOPES`, `SYNC_RUN_STATUSES`) do stay in the view module.
 */
import React from "react";

import { BexioFilterSelect } from "../../../components/admin/bexio/workspace/BexioFilterBar";
import {
  PERIOD_OPTIONS,
  SYNC_RUN_SCOPES,
  SYNC_RUN_STATUSES,
} from "./bexioSyncRunsView";

/**
 * Scope filter options.
 *
 * "Daten-Syncs" is the default and selects exactly the backend's
 * `DATA_SYNC_SCOPES`. The individual entries below it let an operator narrow
 * further or reach the maintenance and technical runs the default hides.
 * `payments` is absent because no such scope exists — see `bexioSyncRunsView`.
 */
export const SCOPE_FILTER_OPTIONS = [
  { value: "data", label: "Umfang: Daten-Syncs" },
  ...SYNC_RUN_SCOPES.map((s) => ({ value: s.value, label: s.label })),
];

export const STATUS_FILTER_OPTIONS = [
  { value: "", label: "Status: Alle" },
  ...SYNC_RUN_STATUSES.map((s) => ({ value: s.value, label: s.label })),
];

export default function BexioSyncRunsFilterBar({
  scopeFilter,
  statusFilter,
  periodDays,
  total,
  onScopeChange,
  onStatusChange,
  onPeriodChange,
}) {
  return (
    <div className="mb-4 rounded-xl border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-3">
        <BexioFilterSelect
          label="Umfang"
          value={scopeFilter}
          onChange={onScopeChange}
          options={SCOPE_FILTER_OPTIONS}
        />
        <BexioFilterSelect
          label="Status"
          value={statusFilter}
          onChange={onStatusChange}
          options={STATUS_FILTER_OPTIONS}
        />
        <BexioFilterSelect
          label="Zeitraum"
          value={String(periodDays)}
          onChange={onPeriodChange}
          options={PERIOD_OPTIONS.map((p) => ({
            value: String(p.days),
            label: p.label,
          }))}
        />
        <span className="ml-auto text-xs text-muted-foreground">
          {typeof total === "number"
            ? `${total} ${total === 1 ? "Lauf" : "Läufe"}`
            : null}
        </span>
      </div>
      {/* Says out loud which of the two filters moves the KPIs, so a reader who
          filters to errors is not surprised that the success rate held still. */}
      <p className="mt-2 text-xs text-muted-foreground">
        Umfang und Zeitraum gelten für Kennzahlen und Tabelle. Der Status-Filter
        betrifft nur die Tabelle.
      </p>
    </div>
  );
}
